import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MAP_CONFIG, MAP_CONTAINER_STYLE } from './constants';
import useBusinessAtAGlance from '../../hooks/useBusinessAtAGlance';
import { useArticle } from '../../hooks/useArticle';
import SearchInput from './components/SearchInput';
import BusinessListItem from './components/BusinessListItem';
import BusinessCard from './components/BusinessCard';
import logo from '../../assets/logo_transparent.png';
import polyline from '@mapbox/polyline';
import './style.css';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { BUSINESS_FIELDS } from './constants';

/**
 * Main BusinessSearch Component
 * Displays a map with business listings and search functionality
 */
const BusinessSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [map, setMap] = useState(null);
  const [travelMode, setTravelMode] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [directionsError, setDirectionsError] = useState('');
  const [requestingDirections, setRequestingDirections] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mapViewState, setMapViewState] = useState({
    bounds: null,
    center: MAP_CONFIG.DEFAULT_CENTER,
    zoom: MAP_CONFIG.DEFAULT_ZOOM,
  });
  const [isMobileSearchModalOpen, setIsMobileSearchModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [directions, setDirections] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articlesError, setArticlesError] = useState(null);
  const mapRef = useRef();
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);

  const { businesses, loading, searchBusinesses, searchBusinessesByBounds } =
    useBusinessAtAGlance(mapViewState.bounds, searchTerm);

  // Update map bounds when map is dragged/zoomed
  const updateMapBounds = useCallback((map) => {
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    setMapViewState(prev => ({
      ...prev,
      bounds: {
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() },
        center: { 
          lat: bounds.getCenter().lat(), 
          lng: bounds.getCenter().lng() 
        },
      },
    }));
  }, []);

  // Handle map load
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMap(map);
    updateMapBounds(map);
  }, [updateMapBounds]);

  // Handle map bounds change with debounce
  const onBoundsChanged = useCallback(() => {
    if (!mapRef.current) return;
    
    const timer = setTimeout(() => {
      updateMapBounds(mapRef.current);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [updateMapBounds]);

  // Handle search input change
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Handle business selection from list
  const handlePlaceSelect = useCallback((business) => {
    setSelectedPlace(business);
    setIsDetailsOpen(true);
    
    // Fetch articles when a business is selected
    if (business?.uid) {
      fetchBusinessArticles(business.uid);
    }

    if (map && business.markerPosition) {
      const { lat, lng } = business.markerPosition;
      map.panTo({ lat, lng });
      map.setZoom(20);
    }
  }, [map]);

  // Handle marker click
  const handleMarkerClick = useCallback((business) => {
    setSelectedPlace(business);
    setIsDetailsOpen(true);
    
    // Fetch articles when a business is selected via marker
    if (business?.uid) {
      fetchBusinessArticles(business.uid);
    }
  }, []);

  // Close details panel
  const closeDetails = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedPlace(null);
  }, []);

  // Navigate to business profile
  const navigateToBusiness = useCallback(() => {
    if (selectedPlace?.shopName) {
      navigate(`/${encodeURIComponent(selectedPlace.shopName)}`);
    }
  }, [selectedPlace, navigate]);

  // Handle getting directions to a business
  const handleGetDirections = useCallback(async (business) => {
    if (!business || !business.markerPosition) return;

    // Clear previous state
    setDirectionsError('');
    setRoutePath([]);
    setRequestingDirections(true);

    // Cycle through travel modes
    setTravelMode(prev => {
      if (prev === null) return 'DRIVE';
      if (prev === 'DRIVE') return 'TWO_WHEELER';
      if (prev === 'TWO_WHEELER') return 'WALK';
      return null;
    });

    // Get user's current location
    if (!navigator.geolocation) {
      setDirectionsError('Geolocation is not supported by your browser');
      setRequestingDirections(false);
      return;
    }

    try {
      // Get current position
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });

      const origin = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      setUserLocation(origin);
      
      const destination = business.markerPosition;
      let travelModeStr = 'DRIVE';
      
      if (travelMode === 'TWO_WHEELER') travelModeStr = 'TWO_WHEELER';
      if (travelMode === 'WALK') travelModeStr = 'WALK';

      // Call Google Maps Routes API
      const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'routes.polyline.encodedPolyline'
        },
        body: JSON.stringify({
          origin: { 
            location: { 
              latLng: { 
                latitude: origin.lat, 
                longitude: origin.lng 
              } 
            } 
          },
          destination: { 
            location: { 
              latLng: { 
                latitude: destination.lat, 
                longitude: destination.lng 
              } 
            } 
          },
          travelMode: travelModeStr,
          routingPreference: 'TRAFFIC_AWARE',
          computeAlternativeRoutes: false,
          languageCode: 'en-US',
          units: 'IMPERIAL'
        })
      });

      const data = await response.json();
      
      if (data.routes?.[0]?.polyline?.encodedPolyline) {
        // Decode the polyline
        const decodedPath = polyline.decode(data.routes[0].polyline.encodedPolyline)
          .map(([lat, lng]) => ({ lat, lng }));
        
        setRoutePath(decodedPath);
        
        // Update map bounds to show the entire route
        if (map) {
          const bounds = new window.google.maps.LatLngBounds();
          decodedPath.forEach(point => bounds.extend(point));
          map.fitBounds(bounds);
        }
      } else {
        setDirectionsError('');
      }
      
    } catch (error) {
      console.error('Error getting directions:', error);
      let errorMessage = 'Could not get directions. ';
      
      if (error.code === error.PERMISSION_DENIED) {
        errorMessage = 'Location access was denied. Please enable location permissions in your device settings.';
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage = 'Location information is currently unavailable. Please check your internet connection and try again.';
      } else if (error.code === error.TIMEOUT) {
        errorMessage = 'The request to get your location timed out. Please try again in an area with better signal.';
      } else if (error.code === 3) { // kCLErrorLocationUnknown
        errorMessage = 'Unable to determine your current location. Please check your internet connection and ensure location services are enabled.';
      } else if (error.message?.includes('No route found')) {
        errorMessage = 'No route could be found between the origin and destination.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else {
        errorMessage = `Unable to get directions (${error.message || 'Unknown error'}). Please try again later.`;
      }
      
      setDirectionsError(errorMessage);
    } finally {
      setRequestingDirections(false);
    }
  }, [map, travelMode]);

  // Fetch articles for a business
  const fetchBusinessArticles = useCallback(async (businessId) => {
    if (!businessId) return;
    
    setLoadingArticles(true);
    setArticlesError(null);
    
    try {
      const db = getFirestore();
      const articlesRef = collection(db, 'articles');
      const q = query(
        articlesRef,
        where('authorId', '==', businessId),
        where('status', '==', 'PUBLISHED')
      );
      
      const querySnapshot = await getDocs(q);
      const articlesList = [];
      
      querySnapshot.forEach((doc) => {
        articlesList.push({ id: doc.id, ...doc.data() });
      });

      setArticles(articlesList);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticlesError('Failed to load articles. Please try again later.');
    } finally {
      setLoadingArticles(false);
    }
  }, []);

  // Initialize directions renderer
  useEffect(() => {
    if (window.google) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#1976D2',
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });
    }
  }, []);

  // Clean up directions renderer on unmount
  useEffect(() => {
    return () => {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
    };
  }, []);

  // Update markers when businesses change
  useEffect(() => {
    if (!map || !businesses?.length) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add new markers
    businesses.forEach((business) => {
      if (!business.markerPosition) return;

      const isSelected = selectedPlace?.uid === business.uid;
      const markerSize = isSelected 
        ? MAP_CONFIG.SELECTED_MARKER_SIZE 
        : MAP_CONFIG.MARKER_SIZE;

      const marker = new window.google.maps.Marker({
        position: business.markerPosition,
        map,
        animation: isSelected ? window.google.maps.Animation.BOUNCE : null,
        icon: {
          url: logo,
          scaledSize: new window.google.maps.Size(markerSize, markerSize),
          origin: new window.google.maps.Point(0, 0),
          anchor: new window.google.maps.Point(markerSize / 2, markerSize),
        },
        zIndex: isSelected ? 1000 : 10,
      });

      marker.addListener('click', () => handleMarkerClick(business));
      markersRef.current.push(marker);
    });

    // Cleanup
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    };
  }, [businesses, map, selectedPlace, handleMarkerClick]);

  // Initialize/Sync search term from URL (?business=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('business') || '';
    setSearchTerm(q);
  }, [location.search]);

  // Update the BusinessCard render to include articles
  const renderBusinessCard = useCallback(() => {
    if (!selectedPlace) return null;
    
    return (
      <BusinessCard
        business={selectedPlace}
        onClose={closeDetails}
        onViewProfile={navigateToBusiness}
        onGetDirections={handleGetDirections}
        travelMode={travelMode}
        isRequestingDirections={requestingDirections}
        directionsError={directionsError}
        articles={articles}
        loadingArticles={loadingArticles}
        articlesError={articlesError}
      />
    );
  }, [selectedPlace, closeDetails, navigateToBusiness, handleGetDirections, 
      travelMode, requestingDirections, directionsError, 
      articles, loadingArticles, articlesError]);

  return (
    <div className="business-search-container">
      {/* Left Panel - Search and List */}
      <div className="left-section">
        <div className="left-panel" onClick={(e) => e.stopPropagation()}>
          <SearchInput 
            value={searchTerm}
            onChange={handleSearch}
            className="p-3"
          />

          <ScrollPanel style={{ width: '100%', height: 'calc(100% - 70px)' }}>
            <div className="suggestions-list">
              {businesses.map((business) => (
                <BusinessListItem
                  key={business.uid}
                  business={business}
                  isSelected={selectedPlace?.uid === business.uid}
                  onClick={handlePlaceSelect}
                />
              ))}
              
              {!loading && businesses.length === 0 && searchTerm.length > 2 && (
                <div className="p-3 text-center text-500">
                  No businesses found matching "{searchTerm}"
                </div>
              )}
              
              {loading && (
                <div className="p-3 text-center">
                  <ProgressSpinner style={{ width: '24px', height: '24px' }} />
                  <span className="ml-2">Searching...</span>
                </div>
              )}
            </div>
          </ScrollPanel>
        </div>

        {/* Details Panel */}
        <div className={`details-panel ${isDetailsOpen ? 'open' : ''}`}>
          {isDetailsOpen && renderBusinessCard()}
        </div>
      </div>

      {/* Mobile Search Modal */}
      <div className={`mobile-search-modal ${isMobileSearchModalOpen ? 'open' : ''}`}>
        <div className="mobile-search-modal-content">
          <div className="mobile-search-modal-header">
            <h3>Search Businesses</h3>
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-text p-button-plain"
              onClick={() => setIsMobileSearchModalOpen(false)}
              aria-label="Close search"
            />
          </div>

          <div className="mobile-search-modal-body">
            <SearchInput
              value={searchTerm}
              onChange={handleSearch}
              className="mb-3"
              placeholder="Search for businesses..."
            />

            <ScrollPanel style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
              <div className="suggestions-list">
                {businesses.map((business) => (
                  <BusinessListItem
                    key={business.uid}
                    business={business}
                    isSelected={selectedPlace?.uid === business.uid}
                    onClick={(business) => {
                      handlePlaceSelect(business);
                      setIsMobileSearchModalOpen(false);
                    }}
                  />
                ))}

                {!loading && businesses.length === 0 && searchTerm.length > 2 && (
                  <div className="p-3 text-center text-500">
                    No businesses found matching "{searchTerm}"
                  </div>
                )}

                {loading && (
                  <div className="p-3 text-center">
                    <ProgressSpinner style={{ width: '24px', height: '24px' }} />
                    <span className="ml-2">Searching...</span>
                  </div>
                )}
              </div>
            </ScrollPanel>
          </div>
        </div>
      </div>

      {/* Right Panel - Map */}
      <div className="right-panel">
        {/* Mobile Search Button */}
        <Button
          icon="pi pi-search"
          className="mobile-search-button p-button-rounded p-button-primary"
          onClick={() => setIsMobileSearchModalOpen(true)}
          aria-label="Search businesses"
        />

        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={mapViewState.center}
          zoom={mapViewState.zoom}
          onLoad={onMapLoad}
          onBoundsChanged={onBoundsChanged}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: false,
            gestureHandling: 'greedy',
            disableDefaultUI: true,
          }}
        >
          {userLocation && (
            <Marker 
              position={userLocation}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: '#FFFFFF'
              }}
            />
          )}
          {routePath.length > 0 && (
            <Polyline
              path={routePath}
              options={{
                strokeColor: '#FF9302',
                strokeOpacity: 0.8,
                strokeWeight: 5,
                icons: [{
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 3,
                    strokeColor: '#884610'
                  },
                  offset: '100%',
                  repeat: '100px'
                }]
              }}
            />
          )}
        </GoogleMap>
      </div>

      {/* Overlay for mobile */}
      {isDetailsOpen && (
        <div 
          className="details-overlay" 
          onClick={closeDetails}
          role="button" 
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && closeDetails()}
        />
      )}
    </div>
  );
};

export default BusinessSearch;
